import { useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "./Logo";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId: string) => {
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/', { replace: true });
      // Wait for navigation and then scroll
      setTimeout(() => {
        const section = document.getElementById(sectionId) || document.querySelector(`[data-section="${sectionId}"]`);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // If on home page, scroll directly
      const section = document.getElementById(sectionId) || document.querySelector(`[data-section="${sectionId}"]`);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsOpen(false); // Close mobile menu
  };

  const scrollToSignup = () => {
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/', { replace: true });
      // Wait for navigation and then scroll
      setTimeout(() => {
        const heroSection = document.querySelector('section');
        if (heroSection) {
          heroSection.scrollIntoView({ behavior: 'smooth' });
          // Focus on the email input after scrolling
          setTimeout(() => {
            const emailInput = document.querySelector('input[type="email"]') as HTMLInputElement;
            if (emailInput) {
              emailInput.focus();
            }
          }, 800);
        }
      }, 100);
    } else {
      // If on home page, scroll directly
      const heroSection = document.querySelector('section');
      if (heroSection) {
        heroSection.scrollIntoView({ behavior: 'smooth' });
        // Focus on the email input after scrolling
        setTimeout(() => {
          const emailInput = document.querySelector('input[type="email"]') as HTMLInputElement;
          if (emailInput) {
            emailInput.focus();
          }
        }, 800);
      }
    }
    setIsOpen(false);
  };

  const navLinks = [
    { label: "Flex Travel", href: "/travel", type: "marketplace", color: "text-primary hover:text-primary/80" },
    { label: "Flex Education", href: "/education", type: "marketplace", color: "text-accent hover:text-accent/80" },
    { label: "Flex Healthcare", href: "/healthcare", type: "marketplace", color: "text-primary/80 hover:text-primary" },
    { label: "How it Works", action: () => scrollToSection("how-it-works"), type: "info", color: "text-accent hover:text-accent/80" },
    { label: "Partners", action: () => scrollToSection("partners"), type: "info", color: "text-primary hover:text-primary/80" },
    { label: "FAQ", action: () => scrollToSection("faq"), type: "info", color: "text-accent hover:text-accent/80" },
    { label: "Contact", href: "/contact", type: "contact", color: "text-primary hover:text-primary/80" },
  ];

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 bg-background backdrop-blur-md border-b border-border shadow-sm"
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Skip to main content link for screen readers */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-primary text-primary-foreground px-4 py-2 rounded-md z-[60]"
      >
        Skip to main content
      </a>
      
      <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
        <div className="flex-shrink-0 mr-8">
          <Logo />
        </div>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">
          {navLinks.map((link) => {
            const baseClasses = "font-medium smooth-transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg px-3 py-2";
            const colorClasses = link.color || "text-foreground/80 hover:text-foreground";
            
            return (
              <div key={link.label}>
                {link.href ? (
                  link.href.startsWith('mailto:') ? (
                    <a 
                      href={link.href}
                      className={`${baseClasses} ${colorClasses}`}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <a 
                      href={link.href}
                      className={`${baseClasses} ${colorClasses}`}
                    >
                      {link.label}
                    </a>
                  )
                ) : (
                  <button
                    onClick={link.action}
                    className={`${baseClasses} ${colorClasses}`}
                  >
                    {link.label}
                  </button>
                )}
              </div>
            );
          })}
          <Button 
            variant="gradient" 
            size="sm" 
            onClick={scrollToSignup}
            aria-label="Join the waitlist for early access"
            className="ml-4"
          >
            Join the Waitlist
          </Button>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Open navigation menu"
                className="p-2"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent 
              side="right" 
              className="w-[280px] bg-background/95 backdrop-blur-md"
            >
              <div className="flex flex-col space-y-6 mt-8">
                <div className="flex justify-between items-center">
                  <Logo />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsOpen(false)}
                    aria-label="Close navigation menu"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
                
                <div className="flex flex-col space-y-3">
                  {navLinks.map((link) => {
                    const mobileBaseClasses = "text-left text-lg font-medium py-3 px-4 smooth-transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg block w-full hover:bg-accent/10";
                    const mobileColorClasses = link.color || "text-foreground/80 hover:text-foreground";
                    
                    return (
                      <div key={link.label}>
                        {link.href ? (
                          link.href.startsWith('mailto:') ? (
                            <a 
                              href={link.href}
                              className={`${mobileBaseClasses} ${mobileColorClasses}`}
                            >
                              {link.label}
                            </a>
                          ) : (
                            <a 
                              href={link.href}
                              onClick={() => setIsOpen(false)}
                              className={`${mobileBaseClasses} ${mobileColorClasses}`}
                            >
                              {link.label}
                            </a>
                          )
                        ) : (
                          <button
                            onClick={link.action}
                            className={`${mobileBaseClasses} ${mobileColorClasses}`}
                          >
                            {link.label}
                          </button>
                        )}
                      </div>
                    );
                  })}
                  
                  <Button 
                    variant="gradient" 
                    onClick={scrollToSignup}
                    className="mt-6 w-full"
                    aria-label="Join the waitlist for early access"
                  >
                    Join the Waitlist
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;